import { ChangeEvent, SyntheticEvent, useState } from "react";
import { DropdownProps, Message, Table } from "semantic-ui-react";

import { useRestClientContext } from "../../../contexts/RestClientContext";

import AddUserVector from "../../../assets/AddUserVector";
import RemoveUserVector from "../../../assets/RemoveUserVector";
import Button from "../Button/Button";
import Input from "../Input/Input";
import Preloader from "../Preloader/Preloader";

import RedactedEmail from "../RedactedEmail/RedactedEmail";
import RoleSelect from "../RoleSelect/RoleSelect";
import Role, { ExtraPermission, extraPermissionNiceName } from "../../../rest/Role";
import Team from "../../../rest/Team";
import { User } from "../../../types/types";
import Select from "../Select/Select";

import "./UserManagementTable.scss";
import { useUserContext } from "../../../contexts/UserContext";

interface UserManagementTableProps {
	team: Team;
}

export default function UserManagementTable({ team }: UserManagementTableProps) {
	const client = useRestClientContext();
	const loggedInUser = useUserContext();

	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error>();

	const [users, setUsers] = useState<User[]>(team.users!);
	const [addUserEmail, setAddUserEmail] = useState<string>("");
	const [addUserRole, setAddUserRole] = useState<Role>(Role.USER);

	const reload = () => {
		setLoading(true);

		client
			.fetchTeam(team.id!)
			.then((response: Response) => response.json())
			.then((json: Team) => setUsers(json.users!))
			.catch((error: Error) => setError(error))
			.finally(() => setLoading(false));
	};

	const onAddUser = () => {
		if (!addUserEmail.length) {
			alert("Please specify an e-mail address");
			return;
		}

		setLoading(true);

		client
			.updateTeamUsers(team.id!, {
				email: addUserEmail,
				role: addUserRole,
			})
			.then(() => {
				reload();
				setAddUserEmail("");
			})
			.catch((error: Error) => alert(error.message))
			.finally(() => setLoading(false));
	};

	const onRemoveUser = (user: User) => {
		if (!window.confirm(`Are you sure you want to remove ${user.email} from this team?`)) return;

		setLoading(true);

		client
			.deleteTeamUser(team.id!, user.id)
			.then(() => reload())
			.catch((error: Error) => alert(error.message))
			.finally(() => setLoading(false));
	};

	const onUpdateUser = (user: User, attributes: Partial<User>) => {
		setLoading(true);

		client
			.updateTeamUser(team.id!, user.id, attributes)
			.then(() => reload())
			.catch((error: Error) => {
				alert(error.message);
			})
			.finally(() => setLoading(false));
	};

	return (
		<div className="user-management-table-container">
			{error && <Message negative>{error.message}</Message>}
			<Table celled>
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell>E-Mail</Table.HeaderCell>
						<Table.HeaderCell>Role</Table.HeaderCell>
						{loggedInUser.is_admin && <Table.HeaderCell>Extra permissions</Table.HeaderCell>}
						<Table.HeaderCell>Actions</Table.HeaderCell>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{users.map((user) => {
						return (
							<Table.Row key={user.id}>
								<Table.Cell>
									<RedactedEmail address={user.email} />
								</Table.Cell>
								<Table.Cell>
									<RoleSelect
										value={user.role}
										onChange={(_e: SyntheticEvent<HTMLElement, Event>, { value }: DropdownProps) =>
											onUpdateUser(user, { role: value as Role })
										}
									/>
								</Table.Cell>
								{loggedInUser.is_admin && (
									<Table.Cell>
										<Select
											multiple
											placeholder="None"
											options={Object.values(ExtraPermission).map((permission) => ({
												key: permission,
												text: extraPermissionNiceName[permission],
												value: permission,
											}))}
											value={user.extra_permissions ?? []}
											onChange={(
												_e: SyntheticEvent<HTMLElement, Event>,
												{ value }: DropdownProps
											) =>
												onUpdateUser(user, {
													role: user.role,
													extra_permissions: (value as string[]) ?? [],
												})
											}
										/>
									</Table.Cell>
								)}
								<Table.Cell>
									<Button variant="contained" onClick={() => onRemoveUser(user)}>
										<RemoveUserVector />
									</Button>
								</Table.Cell>
							</Table.Row>
						);
					})}
					<Table.Row>
						<Table.Cell>
							<Input
								placeholder="E-Mail Address"
								value={addUserEmail}
								disabled={loading}
								onChange={(event: ChangeEvent<HTMLInputElement>) => setAddUserEmail(event.target.value)}
							/>
						</Table.Cell>
						<Table.Cell>
							<RoleSelect
								value={addUserRole}
								disabled={loading}
								onChange={(e: SyntheticEvent<HTMLElement, Event>, { value }: DropdownProps) =>
									setAddUserRole(value as Role)
								}
							/>
						</Table.Cell>
						{loggedInUser.is_admin && <Table.Cell />}
						<Table.Cell>
							<Button variant="outlined" onClick={onAddUser}>
								<AddUserVector />
							</Button>
						</Table.Cell>
					</Table.Row>
				</Table.Body>
			</Table>
			{loading && <Preloader cover />}
		</div>
	);
}
